import { useEffect, useState, useMemo, useLayoutEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { isEmpty, uniqBy } from 'lodash'

import { List, ListItem, ListItemButton, ListItemText, Collapse, Tooltip } from '@mui/material'

import LoadingSpinner from '../../../../../../custom-components/LoadingSpinner'
import SkeletonLoaderSidePanel from '../../../../../../custom-components/skeletons/SkeletonLoaderSidePanel'

import { useTeams } from '../../../hooks/useTeams'

const useStyles = makeStyles(() => ({
	formSidePanel: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		height: '100%',
	},
	formBody: {
		display: 'contents',
	},
	deviceHeading: {
		display: 'flex',
		alignSelf: 'stretch',
		paddingTop: 0.5,
		paddingBottom: 0.5,
		paddingRight: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.04)',
		'&:hover': {
			backgroundColor: 'rgba(0, 0, 0, 0.04)',
		},
		'& .MuiTypography-root': {
			fontWeight: 'bold',
			display: 'block',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			overflow: 'hidden',
		},
	},
	deviceItem: {
		padding: 0,
		display: 'flex',
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'stretch',
	},
	deviceItemBtn: {
		display: 'flex',
		padding: '1px 0px 1px 16px',
		'&:hover': {
			backgroundColor: 'white',
		},
		'& .MuiTypography-root': {
			display: 'block',
			fontSize: '15px',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			overflow: 'hidden',
		},
	},
	deviceItemToolbar: {
		position: 'relative',
		top: 'auto',
		right: 'auto',
		display: 'flex',
		flex: '1 1 auto',
		justifyContent: 'flex-end',
		alignItems: 'flex-start',
		backgroundColor: '#fff',
		transform: 'none',
		paddingRight: '2px',
	},
	all: {
		padding: 0,
		display: 'flex',
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'stretch',
	},
	deviceGroup: {
		padding: 0,
		display: 'flex',
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'stretch',
	},
	deviceGroupToolbar: {
		position: 'relative',
		top: 'auto',
		right: 'auto',
		display: 'flex',
		flex: '1 1 auto',
		justifyContent: 'flex-end',
		alignItems: 'flex-start',
		transform: 'none',
		paddingRight: '2px',
	},
	deviceMenuListContainer: {
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		flex: '1 1 0',
		overflow: 'hidden',
	},
	deviceMenuList: {
		paddingTop: 0,
		overflow: 'hidden',
		height: 'inherit',
		'&:not(.loading):hover': {
			overflowY: 'auto',
		},
		'& .MuiListItem-root': {
			flexDirection: 'column',
		},
		'& .MuiCollapse-root': {
			display: 'flex',
			flexDirection: 'column',
			alignSelf: 'stretch',
		},
		'& .MuiListItem-container': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
		'& .MuiListItem-container:hover .MuiTypography-root': {
			textDecoration: 'underline',
		},
	},
	deviceMenuChildList: {
		'& ul li:hover .MuiTypography-root': {
			textDecoration: 'underline',
		},
	},
	loadingWrapper: {
		position: 'absolute',
		top: '20%',
		left: '45%',
		zIndex: '99999',
	},
}))

export function EmployeeLeftPanel(props) {
	const { customemizedGroupName, selectedMobile, setSelectedMobile } = props
	const [t] = useTranslation('common')
	const { environment } = useSelector((state) => state)
	const classes = useStyles()
	const userRights = environment.userCurrent.rights

	const { loadingDevices } = useTeams()

	const [collapseTeams, setCollapseTeams] = useState([])
	const [itemName, setItemName] = useState(null)
	const [hovering, setHovering] = useState(false)
	const [groupHovering, setGroupHovering] = useState(false)
	const [dispatchFormSelected, setDispatchFormSelected] = useState({})
	const [openSendDispatch, setOpenSendDispatch] = useState(false)
	const [skeletonLoading, setSkeletonLoading] = useState(true)
	const [searchText, setSearchText] = useState('')

	// const customemizedTeams = useMemo(() => {
	// 	if (isEmpty(environment?.teams)) return []

	// 	return environment?.teams?.map((team) => {
	// 		const customizedName = customemizedTeamNames?.[team.key] || team?.name
	// 		return {
	// 			...team,
	// 			name: customizedName,
	// 		}
	// 	})
	// }, [environment?.teams, customemizedTeamNames])

	const filteredDeviceByKeys = useMemo(() => {
		if (searchText === '') {
			return uniqBy(environment?.devices, 'key')
		} else {
			return uniqBy(environment?.devices, 'key').filter(({ name }) =>
				name.toLowerCase().includes(searchText)
			)
		}
	}, [searchText, environment?.devices])

	const dispatch = useDispatch()

	useEffect(() => {
		if (!environment.isProjectFormsLoaded || !environment.projects.length) return
		if (isEmpty(environment.dispatchForm) || !environment.userCurrent?.projectForm) return
		const matchProject = environment.projects.find(
			(item) => item.key === environment.dispatchForm.projectKey
		)
		const matchForm =
			!isEmpty(matchProject) &&
			matchProject.forms.find((item) => item.key === environment.dispatchForm.formKey)
		if (!isEmpty(matchProject) && matchForm) {
			setDispatchFormSelected({ ...matchForm, projectKey: matchProject.key })
		}
	}, [environment.dispatchForm, environment.projects])

	useLayoutEffect(() => {
		if (environment?.teams && environment?.teams.length > 0) {
			setSkeletonLoading(false)
		} else {
			setSkeletonLoading(true)
		}
	}, [environment?.teams])

	const showLoading = () => (
		<>
			<div className={classes.loadingWrapper}>
				<LoadingSpinner withStyle={false} />
			</div>
			{skeletonLoading && <SkeletonLoaderSidePanel />}
		</>
	)

	const handlePopoverOpen = (newItemName) => (event) => {
		event.stopPropagation()
		setHovering((prev) => itemName !== newItemName || !prev)
		setItemName(newItemName)
	}

	const handlePopoverClose = () => {
		if (openSendDispatch) return
		setHovering(false)
	}

	const handleGroupPopoverOpen = (newItemName) => (event) => {
		event.stopPropagation()
		setGroupHovering((prev) => itemName !== newItemName || !prev)
		setItemName(newItemName)
	}

	const handleGroupPopoverClose = () => {
		if (openSendDispatch) return
		setHovering(false)
		setGroupHovering(false)
	}

	const handleOpenSendDispatch = (value) => {
		setOpenSendDispatch(!!value)
	}

	const onChangeValueHandler = (value) => {
		const term = !value ? '' : value.toLowerCase()
		setSearchText(term)
	}

	const handleCollapse = (teamKey) => {
		const found = collapseTeams.includes(teamKey)
		if (!found) {
			setCollapseTeams([...collapseTeams, teamKey])
		} else {
			setCollapseTeams(collapseTeams.filter((item) => item !== teamKey))
		}
	}

	return (
		<div id="form-side-panel" className={classes.formSidePanel}>
			<div className={classes.deviceMenuListContainer}>
				<List
					className={`${classes.deviceMenuList} ${
						loadingDevices ? t('common:misc.loading').toLowerCase() : ''
					}`}
				>
					<ListItem key={'all'} className={classes.deviceGroup}>
						<ListItemButton
							className={classes.deviceHeading}
							onClick={() => setSelectedMobile(null)}
						>
							<ListItemText primary={'All'} />
						</ListItemButton>
					</ListItem>

					<ListItem
						key={'mobileUser'}
						className={classes.deviceGroup}
						ContainerProps={{ onMouseLeave: handlePopoverClose }}
						disablePadding
					>
						<ListItemButton
							className={classes.deviceHeading}
							onClick={() => handleCollapse('mobileUser')}
							onMouseEnter={handleGroupPopoverOpen(`${'mobileUser'}`)}
							onMouseLeave={handleGroupPopoverClose}
						>
							<ListItemText primary={customemizedGroupName || 'Mobile Users'} />
						</ListItemButton>
						<Collapse
							id={'mobileUser_id'}
							in={!collapseTeams.includes('mobileUser')}
							timeout="auto"
							unmountOnExit
						>
							<List className={classes.deviceMenuChildList} disablePadding>
								{filteredDeviceByKeys.map((device) => (
									<ListItem
										key={device.key}
										className={classes.deviceItem}
										onMouseEnter={handlePopoverOpen(`mobileUser / ${device.key}`)}
										ContainerProps={{ onMouseLeave: handlePopoverClose }}
									>
										<ListItemButton
											className={classes.deviceItemBtn}
											disabled={selectedMobile?.key === device?.key}
											onClick={() => setSelectedMobile(device)}
										>
											<ListItemText
												primary={
													<Tooltip
														title={device.number}
														arrow
														placement="bottom-start"
														disableInteractive
														enterDelay={1000}
														enterNextDelay={1000}
													>
														<span>
															{!device.name || isEmpty(device.name) ? device.number : device.name}
														</span>
													</Tooltip>
												}
											/>
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Collapse>
					</ListItem>
				</List>
			</div>
		</div>
	)
}
